import { motion, AnimatePresence } from 'framer-motion'
import { useState, useMemo } from 'react'
import { IPhoneNumber } from '../interface'
import { formatPhoneNumber } from '../utils'
import { PhoneNumberSearchInput } from './PhoneNumberSearchInput'

interface ChangeRealtorNumberViewProps {
   activePhoneNumbers: IPhoneNumber[]
   onSelectNumber: (number: IPhoneNumber) => void
   onCancel: () => void
   currentRealtorNumber?: IPhoneNumber
}

export const ChangeRealtorNumberView = ({
   activePhoneNumbers,
   onSelectNumber,
   onCancel,
   currentRealtorNumber,
}: ChangeRealtorNumberViewProps) => {
   const [searchQuery, setSearchQuery] = useState('')
   const [hoveredNumber, setHoveredNumber] = useState<string | null>(null)

   const filteredNumbers = useMemo(() => {
      if (!searchQuery.trim()) return activePhoneNumbers

      const query = searchQuery.toLowerCase().trim()
      return activePhoneNumbers.filter(
         (number) =>
            number.phoneNumber.phoneNumber.includes(query) ||
            number.phoneNumber.locality?.toLowerCase().includes(query) ||
            number.phoneNumber.friendlyName?.toLowerCase().includes(query)
      )
   }, [activePhoneNumbers, searchQuery])

   const resultLocality = filteredNumbers[0]?.phoneNumber?.locality || ''

   return (
      <motion.div
         initial={{ opacity: 0, y: 20 }}
         animate={{ opacity: 1, y: 0 }}
         exit={{ opacity: 0, y: -20 }}
         transition={{ duration: 0.3 }}
         className="flex flex-col gap-y-6"
      >
         {/* Header Section */}
         <div className="p-4 py-2 bg-white/[0.05] mr-3 rounded-lg">
            <div className="flex items-center justify-between">
               <p className="text-white text-lg font-medium">
                  Change Realtor Call Line
               </p>
               <motion.button
                  key="cancel-button"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onCancel}
                  className="bg-black/20 rounded-[4px] px-6 py-3 text-white hover:text-white/70 font-medium hover:bg-black/30 transition-colors"
               >
                  Cancel
               </motion.button>
            </div>
         </div>

         {/* Search Input */}
         <div className="pr-3">
            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search by number or location"
            />
         </div>

         {/* Results Info */}
         {searchQuery && filteredNumbers.length > 0 && (
            <motion.div
               initial={{ opacity: 0, y: -10 }}
               animate={{ opacity: 1, y: 0 }}
               className="pr-3"
            >
               <p className="text-white/70 text-sm">
                  Showing results for "{searchQuery}"
                  {resultLocality && ` - ${resultLocality}`}
               </p>
            </motion.div>
         )}

         {/* Phone Numbers List */}
         <div className="flex flex-col gap-y-2 pr-3">
            {filteredNumbers.length > 0 ? (
               filteredNumbers.map((number, index) => {
                  const isHovered =
                     hoveredNumber === number.phoneNumber.phoneNumber
                  const isCurrent =
                     currentRealtorNumber?.phoneNumber.phoneNumber ===
                     number.phoneNumber.phoneNumber

                  return (
                     <motion.div
                        key={number.phoneNumber.phoneNumber}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`flex items-center justify-between p-4 py-2 rounded-lg transition-colors ${
                           isCurrent
                              ? 'bg-white/[0.05]'
                              : 'bg-white/[0.05] hover:bg-white/[0.10]'
                        }`}
                        onMouseEnter={() =>
                           setHoveredNumber(number.phoneNumber.phoneNumber)
                        }
                        onMouseLeave={() => setHoveredNumber(null)}
                     >
                        <div className="flex flex-col gap-y-1">
                           <p className="text-white font-medium">
                              {formatPhoneNumber(
                                 number.phoneNumber.phoneNumber
                              )}
                           </p>
                           <p className="text-white/70 text-sm">
                              {number.phoneNumber.locality || 'Unknown'}
                           </p>
                        </div>
                        <AnimatePresence mode="wait">
                           {isCurrent ? (
                              <motion.div
                                 key="current-badge"
                                 initial={{ opacity: 0, scale: 0.8 }}
                                 animate={{ opacity: 1, scale: 1 }}
                                 exit={{ opacity: 0, scale: 0.8 }}
                                 className="px-4 py-2 bg-[#9061DA]/30 rounded-[4px]"
                              >
                                 <p className="text-white text-sm font-medium">
                                    Current
                                 </p>
                              </motion.div>
                           ) : isHovered ? (
                              <motion.button
                                 key="select-button"
                                 initial={{ opacity: 0, x: -10 }}
                                 animate={{ opacity: 1, x: 0 }}
                                 exit={{ opacity: 0, x: -10 }}
                                 transition={{ duration: 0.15 }}
                                 onClick={() => onSelectNumber(number)}
                                 className="w-24 h-10 transition-all border rounded group-hover:block border-primary bg-primary/20 hover:bg-primary"
                              >
                                 Select
                              </motion.button>
                           ) : null}
                        </AnimatePresence>
                     </motion.div>
                  )
               })
            ) : (
               <div className="flex items-center justify-center py-12 pr-3">
                  <p className="text-white/40 text-sm">
                     No numbers found
                     {searchQuery && ` for "${searchQuery}"`}
                  </p>
               </div>
            )}
         </div>
      </motion.div>
   )
}
